<template>
  <div class="container mt-4">

    <!-- FORM -->
    <div class="card shadow-sm my-4">
      <div class="card-header" style="background:#b03a45;">
        <h6 class="mb-0 text-white">
          {{ t("certificateRequest.form.title") }}
        </h6>
      </div>

      <div class="card-body">
        <form @submit.prevent="submitRequest">
          <div class="row">

            <div class="col-md-6 mb-3">
              <label class="form-label">
                {{ t("certificateRequest.form.type") }}
              </label>
              <select v-model="form.certificate_type_id" class="form-select" required>
                <option disabled value="0">
                  {{ t("certificateRequest.form.selectType") }}
                </option>
                <option v-for="tpe in types" :key="tpe.id" :value="tpe.id">
                  {{ tpe.name }}
                </option>
              </select>
            </div>

            <div class="col-md-6 mb-3">
              <label class="form-label">
                {{ t("certificateRequest.form.language") }}
              </label>
              <select v-model="form.language" class="form-select" required>
                <option value="french">
                  {{ t("certificateRequest.languages.french") }}
                </option>
                <option value="arabic">
                  {{ t("certificateRequest.languages.arabic") }}
                </option>
                <option value="english">
                  {{ t("certificateRequest.languages.english") }}
                </option>
              </select>
            </div>

            <div class="col-12 mb-3">
              <label class="form-label">
                {{ t("certificateRequest.form.remarks") }}
              </label>
              <div class="border rounded p-2">
                <textarea
                  v-model="form.remarks"
                  class="form-control border-0"
                  rows="4"
                  :placeholder="t('certificateRequest.form.remarksPlaceholder')"
                ></textarea>
              </div>
            </div>

            <div class="col-12 text-end">
              <button class="btn text-white" style="background:#b03a45;">
                {{ t("certificateRequest.form.submit") }}
              </button>
            </div>

          </div>
        </form>
      </div>
    </div>

    <!-- TABLE -->
    <div class="card mt-5 shadow-sm p-4">
      <h6 class="mb-3">
        {{ t("certificateRequest.table.title") }}
      </h6>

      <table class="table table-striped align-middle">
        <thead>
          <tr>
            <th>{{ t("certificateRequest.table.type") }}</th>
            <th>{{ t("certificateRequest.table.language") }}</th>
            <th>{{ t("certificateRequest.table.status") }}</th>
            <th>{{ t("certificateRequest.table.remarks") }}</th>
            <th>{{ t("certificateRequest.table.date") }}</th>
            <th class="text-center">
              {{ t("certificateRequest.table.actions") }}
            </th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="req in requests" :key="req.id">
            <td>{{ getTypeName(req.certificate_type_id) }}</td>
            <td>{{ t(`certificateRequest.languages.${req.language}`) }}</td>
            <td>
              <span :class="statusClass(req.status)">
                {{ t(`certificateRequest.status.${req.status}`) }}
              </span>
            </td>
            <td>{{ req.remarks || "-" }}</td>
            <td>{{ formatDate(req.created_at) }}</td>

            <td class="text-center">
              <button
                v-if="req.status === 'pending'"
                class="btn btn-sm btn-outline-primary me-1"
                @click="editRequest(req)"
              >
                ✏️
              </button>

              <button
                v-if="req.status === 'pending'"
                class="btn btn-sm btn-outline-danger"
                @click="deleteRequest(req.id)"
              >
                🗑
              </button>
            </td>
          </tr>

          <tr v-if="requests.length === 0">
            <td colspan="6" class="text-center text-muted">
              {{ t("certificateRequest.table.empty") }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import axios from "@/api";
import { useToast } from "vue-toastification";

const { t } = useI18n();
const toast = useToast();

const types = ref([]);
const requests = ref([]);

const form = ref({
  certificate_type_id: 0,
  language: "french",
  remarks: ""
});

const fetchTypes = async () => {
  const res = await axios.get("/certificate-types");
  types.value = res.data.data.data || [];
};

const fetchRequests = async () => {
  const res = await axios.get("/certificate-requests/user/me");
  requests.value = res.data.data.data || [];
};

const submitRequest = async () => {
  try {
    await axios.post("/certificate-requests", form.value);
    toast.success(t("certificateRequest.toast.submitted"));
    form.value = { certificate_type_id: 0, language: "french", remarks: "" };
    fetchRequests();
  } catch {
    toast.error(t("certificateRequest.toast.submitFailed"));
  }
};

const editRequest = (req) => {
  form.value = {
    certificate_type_id: req.certificate_type_id,
    language: req.language,
    remarks: req.remarks || ""
  };
};

const deleteRequest = async (id) => {
  if (!confirm(t("certificateRequest.toast.confirmDelete"))) return;

  try {
    await axios.delete(`/certificate-requests/${id}`);
    toast.success(t("certificateRequest.toast.deleted"));
    fetchRequests();
  } catch (e) {
    toast.error(t("certificateRequest.toast.deleteFailed"));
    console.error(e);
  }
};

const getTypeName = (id) =>
  types.value.find(t => t.id === id)?.name || "-";

const formatDate = d => new Date(d).toLocaleDateString();

const statusClass = (s) => {
  if (s === "pending") return "text-warning fw-bold";
  if (s === "ready") return "text-success fw-bold";
  return "text-danger fw-bold";
};

onMounted(() => {
  fetchTypes();
  fetchRequests();
});
</script>
